import React from 'react'
import PropTypes from 'prop-types'
import { Button, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

/**
 * ReviewCardFooter
 *
 * Renders the action buttons for the Review Card. Shows different button layouts based on
 * whether additional people need details:
 *  - Has new people: Shows single "SAVE AND CONTINUE" button.
 *  - No new people: Shows "SAVE AND CLOSE" + "SAVE AND ATTACH" buttons.
 *
 * @component
 * @param {Object} props
 * @param {Array}    [props.familyMembersToReview=[]] Array of new people to review.
 * @param {Function} props.onSave             Callback fired when user clicks a save button. Receives action: 'close', 'attach', or 'continue'.
 * @returns {JSX.Element}
 */
const ReviewCardFooter = ({ familyMembersToReview = [], onSave }) => {
  const [t] = useTranslation()
  const hasNewPeople = familyMembersToReview.length > 0

  return (
    <>
      {hasNewPeople && (
        <Button
          emphasis="high"
          onClick={() => onSave('continue')}
          fullWidth
          data-testid="review-card-save-continue-button"
          linkName="Review Card Save and Continue"
        >
          {t('review-card.footer.save-and-continue', 'Save and Continue')}
        </Button>
      )}
      {!hasNewPeople && (
        <Row gutters="xs">
          <Button
            emphasis="medium"
            onClick={() => onSave('closeAll')}
            fullWidth
            data-testid="review-card-save-close-button"
            linkName="Review Card Save and Close"
            growX
          >
            {t('review-card.footer.save-and-close', 'Save and Close')}
          </Button>
          <Button
            emphasis="high"
            onClick={() => onSave('attach')}
            fullWidth
            data-testid="review-card-save-attach-button"
            linkName="Review Card Save and Attach"
            growX
          >
            {t('review-card.footer.save-and-attach', 'Save and Attach')}
          </Button>
        </Row>
      )}
    </>
  )
}

ReviewCardFooter.propTypes = {
  familyMembersToReview: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      localizedName: PropTypes.string.isRequired,
    })
  ),
  onSave: PropTypes.func.isRequired,
}

ReviewCardFooter.defaultProps = {
  familyMembersToReview: [],
}

export default ReviewCardFooter
